import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Star, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { PublicNav } from '../components/layout/PublicNav';
import { useAuthStore } from '../store/authStore';
import { feedbackApi } from '../services/api';

const categories = [
  { key: 'general', label: 'General' },
  { key: 'feature', label: 'Feature request' },
  { key: 'bug', label: 'Bug / problem' },
  { key: 'pricing', label: 'Pricing' },
];

export function FeedbackPage() {
  const { user } = useAuthStore();
  const [category, setCategory] = useState('general');
  const [rating, setRating] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!message.trim()) {
      setError('Please tell us a little about your feedback.');
      return;
    }
    setSubmitting(true);
    try {
      await feedbackApi.submit({
        message: message.trim(),
        category,
        rating,
        email: email.trim() || null,
        page: 'feedback-page',
      });
      setDone(true);
    } catch {
      setError('Something went wrong sending your feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-legal-900">
      <PublicNav />

      <div className="pt-28 pb-20 px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto">
        <div className="inline-flex items-center space-x-2 bg-amber-500/10 border border-amber-500/40 rounded-full px-4 py-1.5 text-xs font-semibold text-amber-400 mb-6">
          <MessageSquare className="h-3.5 w-3.5" />
          <span>Urava is in Beta</span>
        </div>

        {done ? (
          <div className="bg-legal-800/50 border border-legal-700 rounded-2xl p-10 text-center">
            <CheckCircle className="h-12 w-12 text-accent mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Thank you 🙏</h1>
            <p className="text-legal-300 mb-8">
              Your feedback goes straight to the team building Urava. It genuinely shapes what we build next.
            </p>
            <Link
              to="/"
              className="inline-flex items-center space-x-2 bg-accent text-legal-900 font-semibold px-6 py-3 rounded-xl hover:bg-accent-400 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to home</span>
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Help us build Urava
            </h1>
            <p className="text-legal-300 mb-8">
              We're early, and you're shaping the product. Tell us what's working, what's missing,
              or what you'd pay for. Every message is read by the team.
            </p>

            {error && (
              <div className="mb-6 p-4 bg-red-900/40 border border-red-700 rounded-xl text-red-300 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-legal-300 mb-2">What's this about?</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setCategory(c.key)}
                      className={`text-sm px-4 py-2 rounded-lg border transition-colors ${
                        category === c.key
                          ? 'border-accent bg-accent text-legal-900 font-semibold'
                          : 'border-legal-700 text-legal-300 hover:border-accent'
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-legal-300 mb-2">
                  How's your experience so far? <span className="text-legal-500">(optional)</span>
                </label>
                <div className="flex space-x-1.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n === rating ? null : n)}
                      aria-label={`${n} star${n > 1 ? 's' : ''}`}
                      className="p-1"
                    >
                      <Star
                        className={`h-7 w-7 transition-colors ${
                          rating && n <= rating ? 'text-accent fill-accent' : 'text-legal-600 hover:text-legal-400'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-legal-300 mb-2">Your feedback</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  maxLength={5000}
                  placeholder="What would make Urava more useful for your practice?"
                  className="w-full rounded-xl bg-legal-800 border border-legal-700 text-white placeholder-legal-500 p-4 text-sm focus:outline-none focus:border-accent resize-none"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-legal-300 mb-2">
                  Email <span className="text-legal-500">(optional — so we can follow up)</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-xl bg-legal-800 border border-legal-700 text-white placeholder-legal-500 p-3.5 text-sm focus:outline-none focus:border-accent"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center space-x-2 bg-accent text-legal-900 font-semibold py-3.5 rounded-xl hover:bg-accent-400 transition-colors disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <span>Send feedback</span>}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
